/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import lodashReduce from 'lodash/reduce';
import { ofType } from 'redux-observable';
import { combineLatest, of } from 'rxjs';
import { mergeMap, pluck, withLatestFrom } from 'rxjs/operators';
import {
    FETCH_AVAILABILITY_DATA_SUCCESS,
    FETCH_MONTHLY_AVAILABILITY_SUCCESS,
} from '../../constants';
import {
    combineAvailability,
    updateCampsiteAvailabilitiesByQueuingLottery,
} from '../../utils/availabilityGridUtil';
import {
    consolidateCampgroundAvailability,
    setAvailabilitiesReservedDates,
} from '../../utils/availabilityReducerUtil';
import { filterActiveEarlyAccessLotteries } from '../../utils/earlyAccessLotteryUtils';

/**
 *
 * @param {Observable} action$ - a stream of actions
 * @param {Observable} state$ - a stream of redux store states
 */
export const fetchMonthlyAvailabilitySuccessEpic = (action$, state$) =>
    action$.pipe(
        ofType(FETCH_MONTHLY_AVAILABILITY_SUCCESS),
        withLatestFrom(
            combineLatest(
                state$.pipe(pluck('campground'), pluck('campground'), pluck('results')),
                state$.pipe(pluck('availabilityGrid')),
                state$.pipe(pluck('earlyAccessLottery'), pluck('lotteries')),
                state$.pipe(pluck('orderDetails'), pluck('reservations')),
                state$.pipe(
                    pluck('internalCamping'),
                    pluck('orderDetails'),
                    pluck('reservations')
                )
            )
        ),
        mergeMap(([action, state]) => {
            const { monthData, isModify } = action;

            const [
                campground,
                availabilityGrid,
                earlyAccessLotteries,
                externalReservations,
                internalReservations,
            ] = state;
            const { availabilitiesByMonth } = availabilityGrid;
            const queueLotteries = get(campground, 'queue_lotteries', []);
            const activeLotteries = filterActiveEarlyAccessLotteries(
                queueLotteries,
                campground.facility_time_zone
            );
            const pendingReservations = isEmpty(externalReservations)
                ? internalReservations
                : externalReservations;
            const reservation = isEmpty(pendingReservations)
                ? null
                : pendingReservations[0];

            const newAvailabilitiesByMonth = consolidateCampgroundAvailability(
                availabilitiesByMonth,
                monthData
            );

            let combinedAvailabilities = combineAvailability(newAvailabilitiesByMonth);

            if (!isEmpty(activeLotteries) && !isEmpty(earlyAccessLotteries)) {
                combinedAvailabilities = lodashReduce(
                    combinedAvailabilities,
                    (accumulated, campsite, campsiteId) => {
                        const newCampsiteAvailabilities =
                            updateCampsiteAvailabilitiesByQueuingLottery({
                                queueLotteries: activeLotteries,
                                userEntries: earlyAccessLotteries,
                                campsiteAvailabilities: campsite.availabilities,
                            });
                        // eslint-disable-next-line no-param-reassign
                        accumulated[campsiteId] = {
                            ...campsite,
                            availabilities: newCampsiteAvailabilities,
                        };
                        return accumulated;
                    },
                    {}
                );
            }

            /* if reservation exists in the redux store, and the use case is modification
                    (e.g. internal/external reservation modify pages), set the reserved dates to
                    "Reserved Available" */
            if (reservation && isModify) {
                combinedAvailabilities = setAvailabilitiesReservedDates(
                    combinedAvailabilities,
                    reservation
                );
            }

            return of({
                type: FETCH_AVAILABILITY_DATA_SUCCESS,
                availabilitiesByMonth: newAvailabilitiesByMonth,
                combinedAvailabilities,
            });
        })
    );
